import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';

interface StudentRedeemPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: {
    id: string;
    username: string;
  };
  teacherId: string;
  onSuccess: () => void;
}

export default function StudentRedeemPointsModal({
  isOpen,
  onClose,
  student,
  teacherId,
  onSuccess,
}: StudentRedeemPointsModalProps) {
  const [points, setPoints] = useState<number>(1);
  const [remark, setRemark] = useState<string>('');
  const [studentTeacherGiftPoints, setStudentTeacherGiftPoints] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // 加载学员的学习U币余额
  useEffect(() => {
    if (isOpen && student.id) {
      loadStudentPoints();
    }
  }, [isOpen, student.id]);

  const loadStudentPoints = async () => {
    try {
      const { data: studentAccount } = await supabase
        .from('user_point_accounts')
        .select('teacher_gift_points')
        .eq('user_id', student.id)
        .eq('role', 'student')
        .maybeSingle();

      setStudentTeacherGiftPoints(studentAccount?.teacher_gift_points || 0);
    } catch (err) {
      console.error('加载学员U币失败:', err);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // 验证输入
    if (points <= 0 || !Number.isInteger(points)) {
      setError('请输入有效的正整数');
      return;
    }

    if (points > studentTeacherGiftPoints) {
      setError(`学员学习U币不足，当前余额：${studentTeacherGiftPoints}`);
      return;
    }

    setLoading(true);

    try {
      // 获取当前会话的 access token
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error('未登录');
      }

      // 调用云函数
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          action: 'student_redeem_points',
          student_id: student.id,
          points,
          remark: remark.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || '兑换失败');
      }

      if (result.success) {
        setSuccess(`成功兑换 ${points} U币`);
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        throw new Error(result.message || '兑换失败');
      }
    } catch (err: any) {
      setError(err.message || '兑换失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                U币兑换 - {student.username}
              </h3>
              <button
                onClick={handleClose}
                disabled={loading}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5 space-y-4">
              {/* Info Card */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                <div className="text-xs text-purple-600 mb-1">学员学习U币余额</div>
                <div className="text-2xl font-bold text-purple-700">{studentTeacherGiftPoints}</div>
                <div className="text-xs text-purple-500 mt-1">仅可兑换教师奖励的学习U币</div>
              </div>

              {/* Points Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  兑换数量 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max={studentTeacherGiftPoints}
                  value={points}
                  onChange={(e) => setPoints(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all"
                  placeholder="请输入兑换数量"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  最多可兑换 {studentTeacherGiftPoints} U币
                </p>
              </div>

              {/* Remark Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  备注说明（可选）
                </label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value.slice(0, 100))}
                  maxLength={100}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all resize-none"
                  placeholder="请输入备注说明（最多100字）"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {remark.length}/100
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700">{success}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || points <= 0 || points > studentTeacherGiftPoints}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    处理中...
                  </>
                ) : (
                  '确认兑换'
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
